import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { useId, type ReactNode } from "react";
import type { FigureLegendEntry } from "@/content";
import { focusRing } from "@/theme/foundations/focus";

type FigureProps = {
  caption: string;
  note?: string;
  legend?: readonly FigureLegendEntry[];
  children: ReactNode;
};

export const Figure = ({ caption, note, legend, children }: FigureProps) => {
  const captionId = useId();

  return (
    <Grid as="figure" className="print-avoid-break" gap="16px">
      {legend && (
        <Flex as="ul" role="list" wrap="wrap" gap="8px 24px" fontSize="small" color="warm.600">
          {legend.map((entry) => (
            <Flex as="li" key={entry.label} align="center" gap="9px">
              <Box
                aria-hidden="true"
                w="18px"
                h="2px"
                borderRadius="1px"
                flexShrink={0}
                bg={entry.tone === "brand" ? "brand.600" : "warm.600"}
              />
              {entry.label}
            </Flex>
          ))}
        </Flex>
      )}

      <Box
        role="group"
        aria-labelledby={captionId}
        tabIndex={0}
        overflowX="auto"
        sx={{ overscrollBehaviorX: "contain", "& svg": { minWidth: { base: 0, lg: "620px" } } }}
        _focusVisible={focusRing}
      >
        {children}
      </Box>

      <Box
        as="figcaption"
        id={captionId}
        fontSize="small"
        lineHeight={1.4}
        color="warm.600"
        sx={{ textWrap: "pretty" }}
      >
        {caption}
        {note && (
          <Text as="span" display="block" mt="4px" color="warm.600">
            {note}
          </Text>
        )}
      </Box>
    </Grid>
  );
};
